// Classic desktop nav overflow (#417). Pure layout maths, kept apart from the
// component so it can be tested without a layout engine.
//
// The desktop row (≥ lg) shows the built-in links and the league's menu items
// in one line, then the sport switcher. When they don't all fit next to the
// logo, the tail of the list collapses into a "More" menu so the switcher
// always stays on screen. Tenants whose links fit render exactly as before.
// A pinned link (core's pinnedNavItem: Register) never goes under More: its
// width is reserved first, and it keeps its place, or sits last in the row
// when its place is under More.

/** Horizontal gap between desktop links (Tailwind `gap-1`). */
export const NAV_LINK_GAP = 4

/** The More button's width before it has been measured once. */
export const NAV_MORE_ESTIMATE = 96

/**
 * How long an overflow must hold, with no resize or link change, before the
 * row collapses. It rides out the cold-load sport restore (basketball's menu,
 * then soccer's) and font swaps.
 */
export const NAV_SETTLE_MS = 200

/**
 * The longest an overflowing row's first paint stays hidden while it waits
 * for its verdict: web fonts that never arrive must not cost the header.
 */
export const NAV_HOLD_MAX_MS = 1000

/**
 * How many leading links fit in `available` px, not counting a pinned one.
 * - All of them when the whole row fits (no More button).
 * - Otherwise the longest prefix that fits together with the More button and
 *   the pinned link (index `pinned`, -1 for none), which is always kept.
 */
export function fitNavItems(widths: number[], available: number, moreWidth: number, gap = NAV_LINK_GAP, pinned = -1): number {
  const all = widths.reduce((sum, w) => sum + w, 0) + gap * Math.max(0, widths.length - 1)
  if (all <= available) return widths.length
  let used = moreWidth + (pinned >= 0 ? gap + widths[pinned] : 0)
  let n = 0
  for (const [i, w] of widths.entries()) {
    if (i === pinned) continue
    const next = used + gap + w
    if (next > available) break
    used = next
    n += 1
  }
  return n
}

/**
 * The row and the More menu for `shown` (from fitNavItems; Infinity: no More).
 * The pinned link stays where it was when its place is in the row, otherwise
 * it comes last, right before More.
 */
export function splitNavLinks<T>(links: readonly T[], shown: number, pinned = -1): { row: T[]; more: T[] } {
  if (shown === Infinity) return { row: [...links], more: [] }
  const rest = links.filter((_, i) => i !== pinned)
  const row = rest.slice(0, shown)
  if (pinned >= 0) row.splice(Math.min(pinned, shown), 0, links[pinned])
  return { row, more: rest.slice(shown) }
}
